const MESSAGE_SELECTOR = "[data-msg-id]";

const positiveId = (value) => {
  const id = Number(value || 0);
  return Number.isFinite(id) && id > 0 ? id : 0;
};

const nonNegativeNumber = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const resolveFeed = (feedOrRef) => {
  if (!feedOrRef) return null;
  if (Object.prototype.hasOwnProperty.call(feedOrRef, "current")) {
    return feedOrRef.current || null;
  }
  return feedOrRef;
};

export function resolveChatInitialPosition(
  meta,
  { focusFirstUnread = false, targetMessageId = null } = {}
) {
  const explicitTargetMessageId = positiveId(targetMessageId);
  const unreadCount = nonNegativeNumber(meta?.unread_count);
  const firstUnreadMessageId = positiveId(meta?.first_unread_message_id);
  const lastReadMessageId = positiveId(meta?.last_read_message_id);
  const lastMessageId = positiveId(meta?.last_message_id);
  const isCaughtUp =
    unreadCount <= 0 ||
    Boolean(lastMessageId && lastReadMessageId >= lastMessageId);

  if (explicitTargetMessageId) {
    return {
      mode: "requested-message",
      messageId: explicitTargetMessageId,
      pendingMessageId: explicitTargetMessageId,
    };
  }

  if (isCaughtUp && !focusFirstUnread) {
    return {
      mode: "bottom",
      messageId: lastMessageId || null,
      pendingMessageId: null,
    };
  }

  if (firstUnreadMessageId) {
    return {
      mode: "first-unread",
      messageId: firstUnreadMessageId,
      pendingMessageId: firstUnreadMessageId,
    };
  }

  if (lastReadMessageId) {
    return {
      mode: "last-read",
      messageId: lastReadMessageId,
      pendingMessageId: null,
    };
  }

  return {
    mode: "bottom",
    messageId: lastMessageId || null,
    pendingMessageId: null,
  };
}

function scrollMessageIntoFeed(feed, element) {
  try {
    const feedRect = feed.getBoundingClientRect?.();
    const messageRect = element.getBoundingClientRect?.();
    if (feedRect && messageRect) {
      const currentScrollTop = Number(feed.scrollTop || 0);
      const clientHeight = Math.max(0, Number(feed.clientHeight || feedRect.height || 0));
      const messageHeight = Math.max(0, Number(messageRect.height || 0));
      const messageTopInContent = currentScrollTop + messageRect.top - feedRect.top;
      const targetScrollTop =
        messageTopInContent - Math.max(24, (clientHeight - messageHeight) * 0.42);
      const maxScrollTop = Math.max(
        0,
        Number(feed.scrollHeight || 0) - clientHeight
      );
      feed.scrollTop = Math.min(Math.max(0, targetScrollTop), maxScrollTop);
      return true;
    }
  } catch {
    // Fall through to the browser-native fallback.
  }

  try {
    element.scrollIntoView({ behavior: "auto", block: "center" });
    return true;
  } catch {
    return false;
  }
}

export function applyChatInitialPosition(
  feedOrRef,
  meta,
  { focusFirstUnread = false, targetMessageId = null } = {}
) {
  const feed = resolveFeed(feedOrRef);
  const position = resolveChatInitialPosition(meta, {
    focusFirstUnread,
    targetMessageId,
  });
  if (!feed) return { ...position, positioned: false };

  if (position.mode === "bottom") {
    try {
      feed.scrollTop = Number(feed.scrollHeight || 0);
      return { ...position, positioned: true };
    } catch {
      return { ...position, positioned: false };
    }
  }

  const target = feed.querySelector?.(`[data-msg-id="${position.messageId}"]`);
  if (target && scrollMessageIntoFeed(feed, target)) {
    return { ...position, pendingMessageId: null, positioned: true };
  }

  // Keep the newest page in a sensible place while older pages are fetched.
  // We deliberately report `positioned: false` so the loading cover remains
  // until the requested unread/read anchor appears (or the fallback timeout).
  try {
    feed.scrollTop = Number(feed.scrollHeight || 0);
  } catch {
    // ignore
  }
  return { ...position, positioned: false };
}

/**
 * Initial chat layout is not stable after the first React paint: lazy images,
 * link cards, the mobile keyboard viewport and the composer can all resize it.
 * Keep applying the same entry anchor briefly, and stop as soon as the user
 * starts interacting so late media never fights a deliberate manual scroll.
 */
export function stabilizeChatInitialPosition({
  feedOrRef,
  meta,
  focusFirstUnread = false,
  targetMessageId = null,
  empty = false,
  onPendingMessage = null,
  onSettled = null,
}) {
  if (typeof window === "undefined") return () => {};

  let stopped = false;
  let settled = false;
  let frameId = null;
  let resizeObserver = null;
  let mutationObserver = null;
  const timerIds = [];
  const feed = resolveFeed(feedOrRef);

  const settle = () => {
    if (settled) return;
    settled = true;
    onSettled?.();
  };

  const apply = () => {
    if (stopped) return;
    const result = applyChatInitialPosition(feedOrRef, meta, {
      focusFirstUnread,
      targetMessageId,
    });
    if (result.pendingMessageId) onPendingMessage?.(result.pendingMessageId);
    if (result.positioned || empty) settle();
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (frameId != null) window.cancelAnimationFrame?.(frameId);
    timerIds.forEach((id) => window.clearTimeout(id));
    resizeObserver?.disconnect?.();
    mutationObserver?.disconnect?.();
    if (feed) {
      feed.removeEventListener?.("pointerdown", stop);
      feed.removeEventListener?.("touchstart", stop);
      feed.removeEventListener?.("wheel", stop);
    }
  };

  const observeMessageRows = () => {
    if (!resizeObserver || !feed) return;
    Array.from(feed.querySelectorAll?.(MESSAGE_SELECTOR) || []).forEach((node) => {
      try {
        resizeObserver.observe(node);
      } catch {
        // ignore unsupported/detached nodes
      }
    });
  };

  if (feed) {
    feed.addEventListener?.("pointerdown", stop, { passive: true });
    feed.addEventListener?.("touchstart", stop, { passive: true });
    feed.addEventListener?.("wheel", stop, { passive: true });

    try {
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => apply());
        // Feed height changes (composer/keyboard/header) and bottom-padding
        // changes do not necessarily resize a message row, but both change the
        // real bottom position.
        resizeObserver.observe(feed);
        observeMessageRows();
      }
      if (typeof MutationObserver !== "undefined") {
        mutationObserver = new MutationObserver(() => {
          observeMessageRows();
          apply();
        });
        mutationObserver.observe(feed, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["style"],
        });
      }
    } catch {
      resizeObserver?.disconnect?.();
      mutationObserver?.disconnect?.();
      resizeObserver = null;
      mutationObserver = null;
    }
  }

  apply();
  frameId = window.requestAnimationFrame(() => {
    frameId = null;
    observeMessageRows();
    apply();
  });

  [80, 220, 450, 900, 1600, 2600, 4500, 7000].forEach((delay) => {
    timerIds.push(window.setTimeout(apply, delay));
  });
  // Never keep the loading cover waiting for an anchor that the API cannot
  // provide in the available history window.
  timerIds.push(window.setTimeout(settle, 900));
  timerIds.push(
    window.setTimeout(() => {
      apply();
      settle();
      stop();
    }, 10000)
  );

  return stop;
}
